/**
 * Modulo M2: percorsi, webinar, iscrizioni, conferme, import, lista di controllo.
 * Schemi condivisi tra API e web. Date sempre come ISO 8601 in UTC nelle risposte.
 */
import { z } from 'zod';
import { codiceFiscaleSchema, emailSchema, passwordSchema } from './auth.js';
import { RUOLI } from './ruoli.js';

export const STATI_PERCORSO = ['bozza', 'pubblicato', 'chiuso'] as const;
export const STATI_WEBINAR = ['bozza', 'pubblicato', 'in_diretta', 'test_aperto', 'chiuso'] as const;
export const MODALITA_INGRESSO = ['regia', 'relatore'] as const;
export const STATI_ISCRIZIONE = ['attiva', 'annullata'] as const;
export const ORIGINI_ISCRIZIONE = ['form', 'import'] as const;
export const STATI_CONFERMA = ['invitato', 'confermato', 'rinunciato'] as const;
export const MODALITA_PARTECIPAZIONE = ['online', 'sala'] as const;
export const STATI_VOCE_CHECKLIST = ['da_fare', 'fatta', 'automatica_ok', 'automatica_errore', 'non_applicabile'] as const;
export const MOMENTI_CHECKLIST = ['-7g', '-1g', '-90min', '-60min', '-30min', 'diretta', 'fine', '+2h'] as const;

export type StatoPercorso = (typeof STATI_PERCORSO)[number];
export type StatoWebinar = (typeof STATI_WEBINAR)[number];
export type ModalitaIngresso = (typeof MODALITA_INGRESSO)[number];
export type StatoConferma = (typeof STATI_CONFERMA)[number];
export type ModalitaPartecipazione = (typeof MODALITA_PARTECIPAZIONE)[number];
export type StatoVoceChecklist = (typeof STATI_VOCE_CHECKLIST)[number];
export type MomentoChecklist = (typeof MOMENTI_CHECKLIST)[number];

const id = z.string().uuid();
const dataIso = z.string().datetime({ offset: true });
const paginazione = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  perPagina: z.coerce.number().int().min(1).max(200).default(50),
});
export type Paginazione = z.infer<typeof paginazione>;
export function paginato<T extends z.ZodTypeAny>(elemento: T) {
  return z.object({ elementi: z.array(elemento), totale: z.number().int(), pagina: z.number().int(), perPagina: z.number().int() });
}

// ---------------------------------------------------------------- Percorsi
export const percorsoInputSchema = z.object({
  titolo: z.string().trim().min(3, 'Il titolo deve avere almeno 3 caratteri.').max(200),
  anno: z.number().int().min(2024).max(2100),
  descrizione: z.string().trim().max(5000).optional().or(z.literal('')),
  organizzatoreEmail: emailSchema,
  iscrizioneAperta: z.boolean().default(false),
});
export type PercorsoInput = z.infer<typeof percorsoInputSchema>;

export const percorsoSchema = z.object({
  id,
  titolo: z.string(),
  anno: z.number().int(),
  descrizione: z.string().nullable(),
  organizzatoreEmail: z.string(),
  iscrizioneAperta: z.boolean(),
  stato: z.enum(STATI_PERCORSO),
  numeroIscritti: z.number().int(),
  numeroWebinar: z.number().int(),
  createdAt: dataIso,
});
export type Percorso = z.infer<typeof percorsoSchema>;

/** Dati pubblici di un percorso, per la pagina di iscrizione (link /registrati?percorso=ID). */
export const percorsoPubblicoSchema = z.object({ id, titolo: z.string(), anno: z.number().int(), descrizione: z.string().nullable(), iscrizioneAperta: z.boolean() });
export type PercorsoPubblico = z.infer<typeof percorsoPubblicoSchema>;

// ---------------------------------------------------------------- Webinar
export const relatoreSchema = z.object({ id, nome: z.string(), cognome: z.string(), ordine: z.number().int() });
export type Relatore = z.infer<typeof relatoreSchema>;

export const webinarInputSchema = z
  .object({
    percorsoId: id,
    titolo: z.string().trim().min(3, 'Il titolo deve avere almeno 3 caratteri.').max(200),
    descrizione: z.string().trim().max(5000).optional().or(z.literal('')),
    inizioPrevisto: dataIso,
    finePrevista: dataIso,
    durataUfficialeMin: z.number().int().min(15).max(600),
    modalitaIngresso: z.enum(MODALITA_INGRESSO).default('regia'),
    sogliaPresenzaPct: z.number().int().min(1).max(100).default(75),
    testDurataMin: z.number().int().min(1).max(120).default(10),
    testFinestraOre: z.number().int().min(1).max(72).default(2),
    relatoriIds: z.array(id).max(10).default([]),
  })
  .refine((w) => new Date(w.finePrevista) > new Date(w.inizioPrevisto), {
    message: "L'orario di fine deve essere dopo quello di inizio.",
    path: ['finePrevista'],
  });
export type WebinarInput = z.infer<typeof webinarInputSchema>;

export const conteggiConfermeSchema = z.object({
  invitati: z.number().int(),
  confermatiOnline: z.number().int(),
  confermatiSala: z.number().int(),
  rinunciati: z.number().int(),
});

export const webinarSchema = z.object({
  id,
  percorsoId: id,
  percorsoTitolo: z.string(),
  titolo: z.string(),
  descrizione: z.string().nullable(),
  inizioPrevisto: dataIso,
  finePrevista: dataIso,
  durataUfficialeMin: z.number().int(),
  inizioEffettivo: dataIso.nullable(),
  fineEffettiva: dataIso.nullable(),
  stato: z.enum(STATI_WEBINAR),
  modalitaIngresso: z.enum(MODALITA_INGRESSO),
  sogliaPresenzaPct: z.number().int(),
  testDurataMin: z.number().int(),
  testFinestraOre: z.number().int(),
  relatori: z.array(relatoreSchema),
  invitiInviatiAt: dataIso.nullable(),
  promemoriaInviatoAt: dataIso.nullable(),
  conteggi: conteggiConfermeSchema,
});
export type Webinar = z.infer<typeof webinarSchema>;

/** Vista del partecipante: il webinar con la propria conferma. */
export const confermaMiaSchema = z.object({
  stato: z.enum(STATI_CONFERMA),
  modalita: z.enum(MODALITA_PARTECIPAZIONE).nullable(),
  confermataAt: dataIso.nullable(),
});
export const webinarPartecipanteSchema = z.object({
  id,
  titolo: z.string(),
  descrizione: z.string().nullable(),
  inizioPrevisto: dataIso,
  finePrevista: dataIso,
  durataUfficialeMin: z.number().int(),
  stato: z.enum(STATI_WEBINAR),
  relatori: z.array(z.object({ nome: z.string(), cognome: z.string() })),
  conferma: confermaMiaSchema.nullable(),
  eOggi: z.boolean(),
});
export type WebinarPartecipante = z.infer<typeof webinarPartecipanteSchema>;

export const percorsoPartecipanteSchema = z.object({
  id,
  titolo: z.string(),
  anno: z.number().int(),
  descrizione: z.string().nullable(),
  iscrittoDal: dataIso,
  webinar: z.array(webinarPartecipanteSchema),
});
export type PercorsoPartecipante = z.infer<typeof percorsoPartecipanteSchema>;

export const confermaInputSchema = z.object({ modalita: z.enum(MODALITA_PARTECIPAZIONE) });

/** Azione dal link nell'email di invito, senza accesso. */
export const azioneLinkConfermaSchema = z.object({ azione: z.enum(['conferma_online', 'conferma_sala', 'rinuncia']) });
export const infoLinkConfermaSchema = z.object({
  nome: z.string(),
  webinar: z.object({ id, titolo: z.string(), inizioPrevisto: dataIso, finePrevista: dataIso }),
  conferma: confermaMiaSchema,
});
export type InfoLinkConferma = z.infer<typeof infoLinkConfermaSchema>;

/** Vista relatore: solo conteggi, nessun dato anagrafico. */
export const webinarRelatoreSchema = z.object({
  id,
  titolo: z.string(),
  percorsoTitolo: z.string(),
  inizioPrevisto: dataIso,
  finePrevista: dataIso,
  stato: z.enum(STATI_WEBINAR),
  modalitaIngresso: z.enum(MODALITA_INGRESSO),
  conteggi: conteggiConfermeSchema,
});
export type WebinarRelatore = z.infer<typeof webinarRelatoreSchema>;

/** Vista regia: dati tecnici del flusso. Gli indirizzi completi arrivano con M3. */
export const webinarRegiaSchema = z.object({
  id,
  titolo: z.string(),
  inizioPrevisto: dataIso,
  finePrevista: dataIso,
  stato: z.enum(STATI_WEBINAR),
  modalitaIngresso: z.enum(MODALITA_INGRESSO),
  streamKey: z.string(),
});
export type WebinarRegia = z.infer<typeof webinarRegiaSchema>;

// ---------------------------------------------------------------- Iscritti (admin)
export const iscrittoSchema = z.object({
  iscrizioneId: id,
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  codiceFiscale: z.string(), // in chiaro: solo per admin, con evento nel registro
  ordine: z.string().nullable(),
  numeroAlbo: z.string().nullable(),
  statoUtente: z.enum(['in_verifica', 'attivo', 'sospeso']),
  statoIscrizione: z.enum(STATI_ISCRIZIONE),
  origine: z.enum(ORIGINI_ISCRIZIONE),
  iscrittoDal: dataIso,
});
export type Iscritto = z.infer<typeof iscrittoSchema>;

export const ricercaIscrittiSchema = paginazione.extend({ q: z.string().trim().max(100).optional(), stato: z.enum(STATI_ISCRIZIONE).optional() });
export const aggiungiIscrittoSchema = z.object({ email: emailSchema });

/** Conferme di un webinar viste dall'admin. */
export const confermaAdminSchema = z.object({
  confermaId: id,
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  stato: z.enum(STATI_CONFERMA),
  modalita: z.enum(MODALITA_PARTECIPAZIONE).nullable(),
  confermataAt: dataIso.nullable(),
});
export type ConfermaAdmin = z.infer<typeof confermaAdminSchema>;
export const ricercaConfermeSchema = paginazione.extend({ q: z.string().trim().max(100).optional(), stato: z.enum(STATI_CONFERMA).optional() });

// ---------------------------------------------------------------- Import
export const COLONNE_IMPORT = ['codiceFiscale', 'nome', 'cognome', 'email', 'ordine', 'numeroAlbo'] as const;
export const rigaImportSchema = z.object({
  codiceFiscale: codiceFiscaleSchema,
  nome: z.string().trim().min(1, 'Nome mancante.').max(100),
  cognome: z.string().trim().min(1, 'Cognome mancante.').max(100),
  email: emailSchema,
  ordine: z.string().trim().max(200).optional().or(z.literal('')),
  numeroAlbo: z.string().trim().max(50).optional().or(z.literal('')),
});
export type RigaImport = z.infer<typeof rigaImportSchema>;

export const erroreRigaSchema = z.object({ riga: z.number().int(), campo: z.string().nullable(), messaggio: z.string() });
export const anteprimaImportSchema = z.object({
  importId: id,
  nomeFile: z.string(),
  righeTotali: z.number().int(),
  righeValide: z.number().int(),
  righeErrate: z.number().int(),
  nuoviUtenti: z.number().int(),
  utentiEsistenti: z.number().int(),
  giaIscritti: z.number().int(),
  errori: z.array(erroreRigaSchema),
  esempio: z.array(rigaImportSchema.extend({ riga: z.number().int(), esito: z.enum(['nuovo', 'esistente', 'gia_iscritto']) })).max(20),
});
export type AnteprimaImport = z.infer<typeof anteprimaImportSchema>;

export const esitoImportSchema = z.object({
  importId: id,
  utentiCreati: z.number().int(),
  iscrizioniCreate: z.number().int(),
  emailAccodate: z.number().int(),
  giaIscritti: z.number().int(),
});
export type EsitoImport = z.infer<typeof esitoImportSchema>;

/** Completamento dell'iscrizione da link email (utenti importati): token lungo, non OTP. */
export const completaIscrizioneSchema = z.object({ token: z.string().min(20).max(200), password: passwordSchema });
export const infoCompletaIscrizioneSchema = z.object({ nome: z.string(), email: z.string(), percorsoTitolo: z.string() });

// ---------------------------------------------------------------- Lista di controllo
export const voceChecklistSchema = z.object({
  id,
  chiave: z.string(),
  momento: z.enum(MOMENTI_CHECKLIST),
  titolo: z.string(),
  automatica: z.boolean(),
  stato: z.enum(STATI_VOCE_CHECKLIST),
  note: z.string().nullable(),
  fattaDa: z.string().nullable(),
  fattaAt: dataIso.nullable(),
  ordine: z.number().int(),
});
export type VoceChecklist = z.infer<typeof voceChecklistSchema>;
export const aggiornaVoceChecklistSchema = z.object({
  stato: z.enum(['da_fare', 'fatta', 'non_applicabile']),
  note: z.string().trim().max(1000).optional().or(z.literal('')),
});

// ---------------------------------------------------------------- Utenti (admin)
export const utenteAdminSchema = z.object({
  id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  ruolo: z.enum(RUOLI),
  stato: z.enum(['in_verifica', 'attivo', 'sospeso']),
  ordine: z.string().nullable(),
  numeroAlbo: z.string().nullable(),
  codiceFiscaleMascherato: z.string(),
  ultimoAccessoAt: dataIso.nullable(),
  createdAt: dataIso,
});
export type UtenteAdmin = z.infer<typeof utenteAdminSchema>;
export const ricercaUtentiSchema = paginazione.extend({ q: z.string().trim().max(100).optional(), ruolo: z.enum(RUOLI).optional() });
export const cambiaRuoloSchema = z.object({ ruolo: z.enum(RUOLI) });

export { paginazione as paginazioneSchema };
